import { BufferGeometry, Color, Points, Vector3 } from "three";
import { Camera } from "three/src/cameras/Camera";
import { LinearEncoding, NearestFilter, RGBAFormat } from "three/src/constants";
import { WebGLRenderer } from "three/src/renderers/WebGLRenderer";
import { WebGLRenderTarget } from "three/src/renderers/WebGLRenderTarget";
import { Scene } from "three/src/scenes/Scene";
import { Viewer } from "./main";
import { eventToPixel } from "./utilities";

export default class GPUPicker {
  private name
  public debug = false
  public scene : Scene
  private viewer: Viewer
  private camera: Camera
  private numSections = 3;
  private sections;
  public target : WebGLRenderTarget
  public needsUpdate = false;
  public result;

  constructor(viewer: Viewer, name, debug=false) {
    this.name = name;
    this.debug = debug;

    this.viewer = viewer;
    this.scene = new Scene();
    this.target = new WebGLRenderTarget(0, 0, {
      minFilter: NearestFilter,
      magFilter: NearestFilter,
      format: RGBAFormat,
      encoding: LinearEncoding,
      generateMipmaps: false
    });

    this.clearSections();
  }

  get height() {
    return this.target.height;
  }

  get width() {
    return this.target.width;
  }

  get renderer(): WebGLRenderer {
    return this.viewer.renderer;
  }

  remove(mesh) {
    this.scene.remove(mesh);
    this.needsUpdate = true;
  }

  add(mesh) {
    this.scene.add(mesh);
    mesh.material.transparent = false;
    this.needsUpdate = true;
  }

  value(event) {
    let uv = eventToPixel(event);
    let tX = uv.x;
    let tY = this.height - uv.y;

    let sectionWidth = Math.floor(this.width/this.numSections);
    let sectionHeight = Math.floor(this.height/this.numSections);
    let sectionX = Math.floor(tX/sectionWidth);
    let sectionY = Math.floor(tY/sectionHeight);
    let sectionIndex = sectionX + (sectionY * this.numSections);

    tX = tX % sectionWidth;
    tY = tY % sectionHeight;
    let pixelIndex = 4 * (tX + tY * sectionWidth);

    if (this.sections[sectionIndex] === null) {
      const timeStart = performance.now();

      let startX = sectionX*sectionWidth;
      let startY = sectionY*sectionHeight;

      let pixelBuffer = new Uint8Array(4 * sectionWidth * sectionHeight);
      this.renderer.readRenderTargetPixels(this.target, startX, startY,
        sectionWidth, sectionHeight, pixelBuffer);

      // Update section results
      this.sections[sectionIndex] = pixelBuffer;

      const timeEnd = performance.now();
      let time = (timeEnd - timeStart).toFixed(1);

      if (this.debug) {
        console.log(`Render ${this.name} picker: ${time}ms`);
      }
    }

    let result = this.sections[sectionIndex];
    if (!result) {
      return null;
    }

    let mouseResult = new Uint8Array([
      result[pixelIndex + 0],
      result[pixelIndex + 1],
      result[pixelIndex + 2],
      result[pixelIndex + 3]
    ]);

    let pickerID = (mouseResult[0] << 24)
      + (mouseResult[1] << 16)
      + (mouseResult[2] << 8)
      + mouseResult[3];

    return (pickerID >= 0) ? pickerID : null;
  }

  update() {
    if (!this.needsUpdate) {
      return;
    }

    this.clearSections();
    this.updateBaseIDs();

    this.needsUpdate = false;

    let oldRenderTarget = this.renderer.getRenderTarget();
    let oldClearColor = this.renderer.getClearColor();
    let oldClearAlpha = this.renderer.getClearAlpha();
    let newClearColor = new Color(0xffffff);

    this.renderer.setRenderTarget(this.target);
    this.renderer.setClearColor(newClearColor);
    this.renderer.clear();
    this.renderer.render(this.scene, this.camera);
    this.renderer.setRenderTarget(oldRenderTarget);
    this.renderer.setClearColor(oldClearColor);
    this.renderer.setClearAlpha(oldClearAlpha);
  }

  clearSections() {
    this.sections = new Array(this.numSections**2).fill(null);
  }

  updateBaseIDs() {
    let currentBaseID = 0;
    this.scene.children.forEach((child: any) => {
      let geometry = child.geometry as BufferGeometry;
      let indices = geometry.attributes.gpu_index.array as any;

      for (var i=0;i<indices.length;i++) {
        indices[i] = i + currentBaseID;
      }

      currentBaseID += indices.length;
      child.geometry.attributes.gpu_index.needsUpdate = true;
      child.geometry.needsUpdate = true;
    });
  }

  setCamera(camera) {
    this.camera = camera;
  }

  resizeTarget(width, height) {
    if ((this.width !== width) || (this.height !== height)) {
      this.target.dispose();
      this.target.setSize(width, height);
    }
  }

  getClosestCoordinate(index) {
    let coordinate = null;

    let object = this.getSelectedObject(index);
    if (object) {
      coordinate = this.getObjectPosition(object, index);
    }

    return coordinate;
  }

  getSelectedObject(index) {
    let object = null;

    this.scene.children.forEach(child => {
      if (object !== null) {
        return;
      }

      if (child instanceof Points) {
        let geometry = child.geometry as BufferGeometry;
        let indices = geometry.attributes.gpu_index.array as Array<number>;

        // Determine if the index exists inside this object
        let minIndex = indices[0];
        let maxIndex = indices[indices.length - 1];
        if ((index < minIndex) || (index > maxIndex)) {
          return;
        }

        // Use the returned index to get the xyz location
        let arrIndex = indices.indexOf(index);
        if (arrIndex === -1) {
          return;
        }

        object = child;
      }
    });

    return object;
  }

  getObjectPosition(mesh, index) {
    let coordinate = null;

    if (mesh instanceof Points) {
      let geometry = mesh.geometry as BufferGeometry;
      let indices = geometry.attributes.gpu_index.array as Array<number>;
      let arrIndex = indices.indexOf(index);
      let positions = geometry.attributes.position;

      coordinate = new Vector3(
        positions.getX(arrIndex) + mesh.position.x,
        positions.getY(arrIndex) + mesh.position.y,
        positions.getZ(arrIndex) + mesh.position.z
      );
    }

    return coordinate;
  }
}
